// src/components/AiDashboard/AiMyNutritionStatus/AiUser/AiUserInputSeparator/AiUserInputMicrophone.js

import React, { useEffect, useRef, useState } from 'react';
import AlertModal from '../../../../payments/server/alertModal';

function getSpeechRecognitionConstructorFromWindow(windowObject) {
  if (!windowObject) return null;
  return (
    windowObject.SpeechRecognition ||
    windowObject.webkitSpeechRecognition ||
    null
  );
}

export default function AiUserInputMicrophone({
  ariaLabel,
  title,
  onTranscriptReady,
  onStatusChange,
  microphoneStatus = 'idle'
}) {
  const [isMicrophoneActive, setIsMicrophoneActive] = useState(false);
  const [isMicrophoneSupported, setIsMicrophoneSupported] = useState(true);
  const [isSpeechRecognitionSupported, setIsSpeechRecognitionSupported] =
    useState(true);
  const [useFirefoxFallback, setUseFirefoxFallback] = useState(false);
  const [microphoneErrorCode, setMicrophoneErrorCode] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const recognitionRef = useRef(null);
  const recognitionStateRef = useRef('idle');
  const latestTranscriptRef = useRef('');

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const hasGrantedPermissionRef = useRef(false);
  const recordingStartTimeRef = useRef(null);

  const onTranscriptReadyRef = useRef(onTranscriptReady);
  useEffect(() => {
    onTranscriptReadyRef.current = onTranscriptReady;
  }, [onTranscriptReady]);

  const onStatusChangeRef = useRef(onStatusChange);
  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  }, [onStatusChange]);

  /**
   * Capability detection + Web Speech recognition setup.
   */
  useEffect(() => {
    if (typeof window === 'undefined') {
      setIsMicrophoneSupported(false);
      setIsSpeechRecognitionSupported(false);
      return;
    }

    const SpeechRecognitionConstructor =
      getSpeechRecognitionConstructorFromWindow(window);

    const hasSpeechRecognitionSupport = !!SpeechRecognitionConstructor;

    const hasMicrophoneApiSupport =
      typeof navigator !== 'undefined' &&
      !!navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === 'function';

    setIsMicrophoneSupported(hasMicrophoneApiSupport);
    setIsSpeechRecognitionSupported(hasSpeechRecognitionSupport);

    // If no Web Speech API but we can record audio → use MediaRecorder fallback
    if (!hasSpeechRecognitionSupport && hasMicrophoneApiSupport) {
      setUseFirefoxFallback(true);
      return;
    }

    if (!SpeechRecognitionConstructor) {
      return;
    }

    const recognition = new SpeechRecognitionConstructor();
    recognition.lang = 'en-US';
    recognition.continuous = false;
    recognition.interimResults = true;

    recognition.onstart = () => {
      recognitionStateRef.current = 'running';
      setIsMicrophoneActive(true);
      setMicrophoneErrorCode(null);
      latestTranscriptRef.current = '';
    };

    recognition.onend = () => {
      recognitionStateRef.current = 'idle';
      setIsMicrophoneActive(false);

      const finalTranscript = String(latestTranscriptRef.current || '').trim();

      if (
        finalTranscript &&
        typeof onTranscriptReadyRef.current === 'function'
      ) {
        onTranscriptReadyRef.current({ text: finalTranscript });
      }

      latestTranscriptRef.current = '';
    };

    recognition.onerror = (event) => {
      recognitionStateRef.current = 'idle';
      setIsMicrophoneActive(false);

      const errorType = event?.error || 'unknown_microphone_error';
      setMicrophoneErrorCode(
        errorType === 'invalid-state' ? 'already_started' : errorType
      );
      setShowErrorModal(true);

      latestTranscriptRef.current = '';
    };

    recognition.onresult = (event) => {
      let transcript = '';
      for (
        let index = event.resultIndex;
        index < event.results.length;
        index += 1
      ) {
        transcript += event.results[index][0].transcript;
      }
      latestTranscriptRef.current = transcript;
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch {
          // ignore cleanup errors
        }
      }
      recognitionRef.current = null;
      recognitionStateRef.current = 'idle';
      latestTranscriptRef.current = '';
    };
  }, []);

  /**
   * Firefox / generic MediaRecorder fallback.
   */
  const startMediaRecorder = async () => {
    audioChunksRef.current = [];

    if (typeof onStatusChangeRef.current === 'function') {
      onStatusChangeRef.current({ status: 'preparing' });
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 48000
        }
      });
      hasGrantedPermissionRef.current = true;

      audioChunksRef.current = [];

      let selectedMimeType = 'audio/webm';
      const codecOptions = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus'
      ];

      for (const codecOption of codecOptions) {
        if (MediaRecorder.isTypeSupported(codecOption)) {
          selectedMimeType = codecOption;
          break;
        }
      }

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: selectedMimeType,
        audioBitsPerSecond: 128000
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());

        const recordingDuration = recordingStartTimeRef.current
          ? Date.now() - recordingStartTimeRef.current
          : 0;

        if (recordingDuration < 500) {
          audioChunksRef.current = [];
          recordingStartTimeRef.current = null;
          setIsMicrophoneActive(false);

          if (typeof onStatusChangeRef.current === 'function') {
            onStatusChangeRef.current({ status: 'idle' });
          }
          return;
        }

        const effectiveMimeType =
          mediaRecorder.mimeType || selectedMimeType || 'audio/webm';

        const currentChunks = [...audioChunksRef.current];
        audioChunksRef.current = [];
        recordingStartTimeRef.current = null;

        const audioBlob = new Blob(currentChunks, {
          type: effectiveMimeType
        });

        if (audioBlob.size === 0 || audioBlob.size < 1000) {
          setIsMicrophoneActive(false);

          if (typeof onStatusChangeRef.current === 'function') {
            onStatusChangeRef.current({ status: 'idle' });
          }
          return;
        }

        const sendAudioToServer = async () => {
          try {
            if (typeof onStatusChangeRef.current === 'function') {
              onStatusChangeRef.current({ status: 'processing' });
            }

            const timestamp = Date.now();
            const randomId = Math.random().toString(36).slice(2, 15);
            const requestId = `${timestamp}_${randomId}`;
            const sessionId = `firefox_${timestamp}_${randomId}`;

            const formData = new FormData();
            formData.append(
              'audio',
              audioBlob,
              `recording_${timestamp}_${randomId}.webm`
            );
            formData.append(
              'audioMimeType',
              audioBlob.type || effectiveMimeType
            );
            formData.append('requestId', requestId);
            formData.append('sessionId', sessionId);
            formData.append('timestamp', timestamp.toString());
            formData.append('blobSize', audioBlob.size.toString());

            const functionUrl =
              'https://us-central1-purehealthnexus.cloudfunctions.net/speechToText';

            const urlWithCacheBuster = `${functionUrl}?t=${timestamp}&rid=${requestId}&sid=${sessionId}&nocache=${randomId}`;

            const response = await fetch(urlWithCacheBuster, {
              method: 'POST',
              body: formData,
              headers: {
                'Cache-Control': 'no-cache, no-store, must-revalidate, max-age=0',
                Pragma: 'no-cache',
                Expires: '0',
                'X-Request-ID': requestId,
                'X-Session-ID': sessionId
              },
              cache: 'no-store'
            });

            if (!response.ok) {
              const errorBody = await response.text();
              throw new Error(
                `speechToText HTTP ${response.status}: ${errorBody}`
              );
            }

            const result = await response.json();
            const transcript = result?.transcript || '';

            if (
              transcript &&
              typeof onTranscriptReadyRef.current === 'function'
            ) {
              onTranscriptReadyRef.current({ text: transcript });
            }

            // NOTE: Do NOT set status to 'idle' here - parent will do it when text appears in input
          } catch {
            setMicrophoneErrorCode('network');
            setShowErrorModal(true);

            // Reset to idle on error
            if (typeof onStatusChangeRef.current === 'function') {
              onStatusChangeRef.current({ status: 'idle' });
            }
          }
        };

        sendAudioToServer();

        setIsMicrophoneActive(false);
        mediaRecorderRef.current = null;
      };

      mediaRecorder.start(100);
      mediaRecorderRef.current = mediaRecorder;

      recordingStartTimeRef.current = Date.now();

      setIsMicrophoneActive(true);
      setMicrophoneErrorCode(null);

      // Emit status: recording started
      if (typeof onStatusChangeRef.current === 'function') {
        onStatusChangeRef.current({ status: 'recording' });
      }

      // After 2 seconds, update status to indicate user can now speak
      setTimeout(() => {
        if (
          mediaRecorderRef.current?.state === 'recording' &&
          typeof onStatusChangeRef.current === 'function'
        ) {
          onStatusChangeRef.current({ status: 'ready_to_speak' });
        }
      }, 2000);
    } catch {
      setMicrophoneErrorCode('permission_denied');
      setShowErrorModal(true);
      setIsMicrophoneActive(false);

      audioChunksRef.current = [];
      recordingStartTimeRef.current = null;

      if (typeof onStatusChangeRef.current === 'function') {
        onStatusChangeRef.current({ status: 'idle' });
      }
    }
  };

  const stopMediaRecorder = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      try {
        mediaRecorderRef.current.requestData();
      } catch {
        // ignore requestData errors
      }
      mediaRecorderRef.current.stop();
      // Immediately update UI state to remove green color
      setIsMicrophoneActive(false);
    }
  };

  /**
   * Start microphone based on browser capabilities (Web Speech or MediaRecorder fallback).
   */
  const startMicrophone = async (event) => {
    // Ignore right-click (button 2) and middle-click (button 1)
    if (event && 'button' in event && event.button !== 0) {
      return;
    }

    if (event?.type === 'touchstart') {
      event.preventDefault();
    }

    if (isMicrophoneActive) {
      return;
    }

    // Fallback path
    if (useFirefoxFallback) {
      // First click: ask for permission only, then user presses again to record
      if (!hasGrantedPermissionRef.current) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true
          });
          hasGrantedPermissionRef.current = true;
          stream.getTracks().forEach((track) => track.stop());
          return;
        } catch {
          setMicrophoneErrorCode('permission_denied');
          setShowErrorModal(true);
          return;
        }
      }

      await startMediaRecorder();
      return;
    }

    // Standard Web Speech API path
    if (!isMicrophoneSupported) {
      setMicrophoneErrorCode('microphone_api_not_supported');
      setShowErrorModal(true);
      return;
    }

    if (!isSpeechRecognitionSupported || !recognitionRef.current) {
      setMicrophoneErrorCode('speech_recognition_not_supported');
      setShowErrorModal(true);
      return;
    }

    if (
      recognitionStateRef.current === 'starting' ||
      recognitionStateRef.current === 'running' ||
      recognitionStateRef.current === 'stopping'
    ) {
      return;
    }

    try {
      recognitionStateRef.current = 'starting';
      recognitionRef.current.start();
    } catch (error) {
      recognitionStateRef.current = 'idle';

      const message = error?.message || '';
      if (
        message.includes('recognition has already started') ||
        message.includes('already started')
      ) {
        setMicrophoneErrorCode('already_started');
      } else {
        setMicrophoneErrorCode('microphone_start_failed');
      }
      setShowErrorModal(true);
    }
  };

  const stopMicrophone = (event) => {
    // Ignore right-click (button 2) and middle-click (button 1)
    if (event && 'button' in event && event.button !== 0) {
      return;
    }

    if (useFirefoxFallback) {
      stopMediaRecorder();
      return;
    }

    if (!isMicrophoneSupported || !recognitionRef.current) {
      return;
    }

    if (
      recognitionStateRef.current !== 'running' &&
      recognitionStateRef.current !== 'starting'
    ) {
      return;
    }

    try {
      recognitionStateRef.current = 'stopping';
      recognitionRef.current.stop();
    } catch {
      setMicrophoneErrorCode('microphone_stop_failed');
      setShowErrorModal(true);
      recognitionStateRef.current = 'idle';
    }
  };

  /**
   * Handle keyboard events (press and hold space/enter).
   */
  const onMicrophoneKeyDown = (event) => {
    if (
      event.key === ' ' ||
      event.key === 'Spacebar' ||
      event.key === 'Enter' ||
      event.code === 'Space' ||
      event.code === 'Enter'
    ) {
      event.preventDefault();
      if (!isMicrophoneActive) {
        void startMicrophone(event);
      }
    }
  };

  const onMicrophoneKeyUp = (event) => {
    if (
      event.key === ' ' ||
      event.key === 'Spacebar' ||
      event.key === 'Enter' ||
      event.code === 'Space' ||
      event.code === 'Enter'
    ) {
      event.preventDefault();
      stopMicrophone();
    }
  };

  const isMicrophoneDisabled =
    !isMicrophoneSupported ||
    (!useFirefoxFallback && !isSpeechRecognitionSupported);

  const getErrorMessage = () => {
    if (!microphoneErrorCode) return '';

    switch (microphoneErrorCode) {
      case 'microphone_api_not_supported':
        return 'Your browser does not support microphone access via the standard media devices API.';
      case 'speech_recognition_not_supported':
        return 'Speech recognition is not supported in this browser. Please use Chrome or Edge for voice input, or type your meal instead.';
      case 'permission_denied':
      case 'not-allowed':
        return 'Microphone access was denied. Please allow microphone access in your browser site settings and try again.';
      case 'no-speech':
        return 'No speech was detected. Please try again.';
      case 'audio-capture':
        return 'No microphone was found. Please ensure a microphone is connected.';
      case 'network':
        return 'A network or server error occurred while converting speech to text. Please try again.';
      case 'already_started':
        return 'Microphone is already listening. Please release and press again to restart.';
      case 'microphone_start_failed':
      case 'microphone_stop_failed':
        return 'There was a problem starting or stopping the microphone. Please try again.';
      default:
        return `Microphone error: ${microphoneErrorCode}`;
    }
  };

  return (
    <>
      {/* Microphone button: press and hold to speak */}
      <button
        type="button"
        className={`aiu-mic ${
          microphoneStatus === 'recording' ? 'aiu-mic-connecting' : ''
        } ${
          microphoneStatus === 'ready_to_speak' ? 'aiu-mic-recording' : ''
        } ${isMicrophoneDisabled ? 'aiu-mic-disabled' : ''}`}
        aria-label={ariaLabel || 'Press and hold to speak'}
        title={
          useFirefoxFallback
            ? 'Press and hold to speak (voice recording mode)'
            : !isSpeechRecognitionSupported
            ? 'Speech recognition is not available in this browser'
            : !isMicrophoneSupported
            ? 'Microphone access is not available in this browser'
            : title || 'Press and hold to speak'
        }
        aria-pressed={isMicrophoneActive}
        onMouseDown={startMicrophone}
        onMouseUp={stopMicrophone}
        onMouseLeave={stopMicrophone}

        onTouchStart={startMicrophone}
        onTouchEnd={stopMicrophone}
        onTouchCancel={stopMicrophone}
        onKeyDown={onMicrophoneKeyDown}
        onKeyUp={onMicrophoneKeyUp}
        onContextMenu={(e) => e.preventDefault()}
        disabled={isMicrophoneDisabled}
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 256 256"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M128,176a48.05,48.05,0,0,0,48-48V64a48,48,0,0,0-96,0v64A48.05,48.05,0,0,0,128,176ZM96,64a32,32,0,0,1,64,0v64a32,32,0,0,1-64,0Zm40,143.6V232a8,8,0,0,1-16,0V207.6A80.11,80.11,0,0,1,48,128a8,8,0,0,1,16,0,64,64,0,0,0,128,0,8,8,0,0,1,16,0A80.11,80.11,0,0,1,136,207.6Z" />
        </svg>
      </button>

      {/* Error modal */}
      <AlertModal
        open={showErrorModal}
        title="Microphone or Speech Recognition Error"
        message={getErrorMessage()}
        variant="error"
        primaryAction={{
          label: 'OK',
          onClick: () => {
            setShowErrorModal(false);
            setMicrophoneErrorCode(null);
          }
        }}
        onClose={() => {
          setShowErrorModal(false);
          setMicrophoneErrorCode(null);
        }}
        requester={{ entityId: 'microphone-input' }}
      />
    </>
  );
}
